import { useState, FormEvent } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// NEWSLETTER CONFIGURATION
// Edit these fields to customise the newsletter section.
// Set subscriberCount to '' to hide it. Set sampleIssueUrl to '' to hide it.
// ─────────────────────────────────────────────────────────────────────────────
const NEWSLETTER_CONFIG = {
  headline: 'Get Weekly AI Tool Picks',
  benefitStatement:
    'Every Friday we hand-pick the best new AI tools, hidden gems, and the one workflow tip that actually saves you time.',
  subscriberCount: '', // e.g. '2,400+' — leave empty to hide
  sampleIssueUrl: '', // e.g. '/blog/sample-issue' — leave empty to hide
  buttonLabel: 'Subscribe Free',
  privacyNote: 'No spam. Unsubscribe anytime.',
};

export default function EmailSignup() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/d6r4t4lsbgrc47fime60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (response.ok) {
        setShowSuccess(true);
        setEmail('');
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-[#0a0a0a] py-20 px-6 border-t border-[#1a1a1a]">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {NEWSLETTER_CONFIG.headline}
        </h2>

        {/* Benefit statement */}
        <p className="text-gray-400 text-base leading-relaxed mb-5 max-w-xl mx-auto">
          {NEWSLETTER_CONFIG.benefitStatement}
        </p>

        {/* Social proof + sample issue row — only shown when configured */}
        {(NEWSLETTER_CONFIG.subscriberCount || NEWSLETTER_CONFIG.sampleIssueUrl) && (
          <div className="flex items-center justify-center gap-5 mb-7 flex-wrap">
            {NEWSLETTER_CONFIG.subscriberCount && (
              <span className="inline-flex items-center gap-1.5 text-sm text-teal-400 font-medium">
                <i className="ri-group-line text-base"></i>
                {NEWSLETTER_CONFIG.subscriberCount} subscribers
              </span>
            )}
            {NEWSLETTER_CONFIG.sampleIssueUrl && (
              <a
                href={NEWSLETTER_CONFIG.sampleIssueUrl}
                className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-teal-300 transition-colors cursor-pointer"
              >
                <i className="ri-mail-open-line text-base"></i>
                See a sample issue
              </a>
            )}
          </div>
        )}

        <form
          id="newsletter_subscription"
          data-readdy-form
          onSubmit={handleSubmit}
          className="max-w-md mx-auto"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={isSubmitting}
              className="flex-1 px-5 py-3.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-7 py-3.5 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {isSubmitting ? 'Subscribing...' : NEWSLETTER_CONFIG.buttonLabel}
            </button>
          </div>

          {error && <p className="mt-3 text-red-400 text-sm">{error}</p>}

          {showSuccess && (
            <div className="mt-4 p-4 bg-teal-500/10 border border-teal-500/30 rounded-lg">
              <p className="text-teal-400 font-medium text-sm">
                ✓ Successfully subscribed! Check your inbox for confirmation.
              </p>
            </div>
          )}
        </form>

        <p className="text-gray-500 text-xs mt-4">{NEWSLETTER_CONFIG.privacyNote}</p>
      </div>
    </section>
  );
}
